import { Button } from "@mui/material";
import React, { useEffect, useState, useRef, useCallback } from "react";
import Input from "../../components/shared/Input";
import Keyboard from "../../components/shared/Keyboard";
import Timer from "../../components/shared/Timer";
import styles from "./QuickGame.module.css";

type Player = {
  id: number;
  name: string;
  position: string;
  age: string;
  number: string;
  photo: string;
  rating: number;
};

type Guess = {
  letter: string,
  status: 'unchecked' | 'correct' | 'outOfPlace' | 'incorrect'
};

const QuickGame = () => {
    const ws = useRef<WebSocket | null>(null);
    const wsIdRef  = useRef<string | null>(null);
    const [lobbyUsers, setLobbyUsers] = useState<{id: string, username: string}[]>([]);
    const [userScores, setUserScores] = useState<{id: string, username: string, score: number}[]>([]);
    const [buttonCheck, setButtonCheck] = useState(true);
    const [currentItem, setCurrentItem] = useState<string | null>(null);
    const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
    const [gameOver, setGameOver] = useState(true);
    const [guesses, setGuesses] = useState<Guess[][]>(new Array(1).fill([]));
    const [currentGuessIndex, setCurrentGuessIndex] = useState(0);
    const [disabledKeys, setDisabledKeys] = useState<string[]>([]);
    const [gameWon, setGameWon] = useState(false);
    const [score, setScore] = useState(0);
    const [username, setUsername] = useState('');
    const [roundCount, setRoundCount] = useState(0);
    const [matchOver, setMatchOver] = useState(false);

    // Function to handle WebSocket messages
    const handleMessage = (event: MessageEvent) => {
      // // console.log("Received a message: ", event.data);  // New log
      const response = JSON.parse(event.data);
      switch (response.type) {
        case 'join':
            // // console.log("Setting wsId to ", response.id);
            // // console.log("user ", response.username);
            // // console.log("Message from server: ", response.message);
            // // console.log("Received join message from server, wsId:", wsIdRef.current);
            wsIdRef.current = response.id;  // Store the id
            break;
        case 'updateUsers':
            console.log("Received updateUsers message from server: ", response.users);
            console.log(response.users.length);
            
            setLobbyUsers(response.users);
            setUserScores(response.scores || []);
            if(response.myUsername) {
              setUsername(response.myUsername);
            }
            break;
        case 'startRound':
          setGuesses(new Array(1).fill([]));
          setCurrentGuessIndex(0);
          setDisabledKeys([]);
          setGameOver(true);
          setGameOver(false);
          setGameWon(false);
          console.log(roundCount);
          setRoundCount(prevRoundCount => prevRoundCount + 1);
          if (response.item && response.item.name) {
            // console.log("Received startRound message from server: ", response.item);
            let newPlayerName = response.item.name;
            newPlayerName = newPlayerName.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
            if(newPlayerName.includes('. ')){
              const nameParts = newPlayerName.split('. ');
              newPlayerName = nameParts[1];
            }
            setSelectedPlayer({ ...response.item, name: newPlayerName });
            setCurrentItem(response.item);
            setTimeout(() => setCurrentItem(null), 15000);
          } else {
            // console.log("Invalid startRound message from server:", response.item);
          }
          break;
        case 'scoreUpdate':
          setUserScores(response.scores || []);
          break;
        default:
            // console.log("Unhandled message type: ", response.type);  // New log
      }
    };

    const handleKeyPress = useCallback((key: string) => {
      if(gameOver || !selectedPlayer) return;
      if (key === '<-') {
        setGuesses(prevGuesses => {
          const newGuesses = [...prevGuesses];
          newGuesses[currentGuessIndex] = newGuesses[currentGuessIndex].slice(0, -1);
          return newGuesses;
        });
      } else if (guesses[currentGuessIndex].length < selectedPlayer.name.length) {
        setGuesses(prevGuesses => {
          const newGuesses = [...prevGuesses];
          newGuesses[currentGuessIndex] = [...newGuesses[currentGuessIndex], { letter: key, status: 'unchecked' as const }];
          return newGuesses;
        });
      }    
    }, [guesses, currentGuessIndex, selectedPlayer, setGuesses, gameOver]);

    useEffect(() => {
      // Listener for page unload event
      // console.log('Component mounted');  // New log
      const handleBeforeUnload = () => {
          if (ws.current && wsIdRef.current) {
            const leaveMessage = JSON.stringify({ type: 'leave', id: wsIdRef.current });
            ws.current.send(leaveMessage);
          }
      };
    
      window.addEventListener('beforeunload', handleBeforeUnload);
    
      return () => {
        // console.log('Component unmounted');  // New log
        window.removeEventListener('beforeunload', handleBeforeUnload);
        if (ws.current) {
          ws.current.close();
        }
      };
    }, [ws, wsIdRef]);

    useEffect(() => {
      if (!ws.current) {
        //const webSocket = new WebSocket('ws://localhost:5000'); // Use your server address here
        const webSocket = new WebSocket('wss://web-production-6f24.up.railway.app/'); // Use your server address here
        // const webSocket = new WebSocket('wss://web-production-6f24.up.railway.app/'); // Use your server address here
        webSocket.onmessage = handleMessage;
        ws.current = webSocket;
        webSocket.onopen = () => {
          // console.log('WebSocket connection opened');
        };
        webSocket.onclose = () => {
          // console.log('WebSocket connection closed');
        };
        // console.log('Websocket created: ', webSocket);  // New log
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      const handleKeyDown = (event: { key: string; }) => {
        if(gameOver) return;
        const upperKey = event.key.toUpperCase();
        // console.log(upperKey);

        if (upperKey === 'ENTER') {
          handleSubmit();
        } else if (upperKey === 'BACKSPACE') {
          handleKeyPress('<-');
        } else if (upperKey.length > 1){
          return;
        } else {
          handleKeyPress(upperKey);
        }
      };
      window.addEventListener('keydown', handleKeyDown);

      return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleKeyPress]);
    
    const handleSearch = () => {
      if (ws.current) {        
        const joinMessage = JSON.stringify({ type: 'join'});
        ws.current.send(joinMessage);
        // console.log('Sending join message to server, wsId:', wsIdRef.current);
        setButtonCheck(false);
      }
    };

    const handleScoreChange = (newScore: number) => {
      console.log(newScore);
      localStorage.setItem('timerScore', JSON.stringify(newScore));
    };

    const handleSubmitRequest = () => {
      let delay = 100;
      setTimeout(function() {
          handleSubmit();
      }, delay);
    }

    const handleSubmit = () => {
      // Perform the letter-by-letter comparison
      if (selectedPlayer) {
        const currentGuess = guesses[currentGuessIndex];
        let playerName = selectedPlayer.name.toUpperCase();
        // Treat hyphens as spaces
        playerName = playerName.replace(/-/g, ' ');
    
        const playerNameNoSpaces = playerName.replace(/ /g, '');
        const guessNoSpaces = currentGuess.map(g => g.letter).join('').replace(/ /g, '');

        // console.log(playerNameNoSpaces);
        
    
        const updatedGuess = Array.from(playerName).map((char, index) => {
          if (char === ' ') {
            return null;
          } else {
            const adjustedIndex = Array.from(playerName.slice(0, index)).filter((char) => char !== ' ').length;
            const letter = guessNoSpaces[adjustedIndex];
            
            if (playerNameNoSpaces.includes(letter)) {
              if (playerNameNoSpaces[adjustedIndex] === letter) {
                // The letter is in the correct position
                return { letter, status: 'correct' as const };
              } else {
                // The letter exists but is in the wrong position
                return { letter, status: 'outOfPlace' as const };
              }
            } else {
              // The letter does not exist in the player's name
              return { letter, status: 'incorrect' as const };
            }
          }
        }).filter(guess => guess !== null) as Guess[];
    
        // Update the current guess with the comparison results
        setGuesses(prevGuesses => {
          const newGuesses = [...prevGuesses];
          newGuesses[currentGuessIndex] = updatedGuess;
          return newGuesses;
        });
  
        const incorrectKeys = updatedGuess.filter(guess => guess.status === 'incorrect').map(guess => guess.letter);
  
  
        setDisabledKeys(prevKeys => [...prevKeys, ...incorrectKeys]);
  
        // console.log('Disabled keys: ', disabledKeys);  // Add this line to log the disabled keys.
        
        const allCorrect = updatedGuess.every(guess => guess.status === 'correct');
        const gameOverStatus = (allCorrect && currentGuessIndex <= 5) || (currentGuessIndex === 4 && !allCorrect);
        const gameWonStatus = allCorrect && currentGuessIndex <= 5;
        setGameOver(gameOverStatus);
        setGameWon(gameWonStatus);
        
        if (gameOverStatus) {
          const timerScoreRef = localStorage.getItem('timerScore');
          let finalScore = Number(timerScoreRef);
          let finalScoretoFixed = Math.ceil(finalScore);
          setScore(score + finalScoretoFixed);
          console.log(score);
      
          
                
          localStorage.setItem('timerScore', '0');
          if (finalScoretoFixed) {
            console.log(finalScoretoFixed);
            const scoreMessage = JSON.stringify({ type: 'scoreUpdate', id: wsIdRef.current, amount: finalScoretoFixed });
            // Set the delay (in milliseconds)
            let delay = 1000; // 2 seconds
    
            // Use setTimeout to delay sending the message
            setTimeout(function() {
                // Use the nullish coalescing operator to ensure ws.current is not null
                ws.current?.send(scoreMessage);
                // console.log('Sending score update message to server');  // Uncomment this if you want to see a console log when the message is sent
            }, delay);
          }
        }
      }
    };

    useEffect(() => {
      if(roundCount > 10){
        setMatchOver(true);
        // setButtonCheck(true);
        // Listener for page unload event
        // console.log('Component mounted');  // New log
        console.log(ws.current , wsIdRef.current);
        
        if (ws.current) {
          const leaveMessage = JSON.stringify({ type: 'leave', id: wsIdRef.current });
          ws.current.send(leaveMessage);
        }
        // setButtonCheck(true);
      }
    }, [roundCount]);
    
    return (
      <div className={styles.mainContent} style={{display: "flex", flexDirection: "column", alignItems: "center", width: "100%", height: "80vh"}}>
          <div style={{display: "flex", justifyContent: "space-between", width: "100%"}}>
            <div>
              <h2>Lobby Users:</h2>
              {lobbyUsers && lobbyUsers.map((user, index) => (
                <div key={index}>
                  <p style={{color: 'white'}}>
                    {user.username}
                    <span> - Score: {userScores.find(score => score.id === user.id)?.score || 0}</span>
                    {username === user.username && '🧍‍♂️'}
                  </p>
                </div>
              ))}
            </div>
            <div style={{marginTop: "8vh"}}>
              <Timer start={!gameOver && !buttonCheck} stop={gameOver || gameWon} resetKey={selectedPlayer ? selectedPlayer.id : -1} width="100px" height="100px" onScoreChange={handleScoreChange} />
            </div>
          </div>

          {(buttonCheck || matchOver) && (
          <div style={{display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexGrow: 1}}>
              <p>QuickGame</p>
              <Button onClick={handleSearch} disabled={!buttonCheck}>Search</Button>
          </div>
          )}

          {(!buttonCheck && !matchOver)&& (
            <div  style={{background: "#333"}}>
              {(currentItem && selectedPlayer) && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <h2>Round {roundCount}</h2>
                  <img src={selectedPlayer.photo} alt={selectedPlayer.name}/>
                  {guesses.map((guess, index) => (
                    <Input 
                      inputValue={guess.map(g => g.letter).join('')} 
                      playerName={selectedPlayer.name} 
                      disabled={index !== currentGuessIndex}
                      guesses={guess}
                      gameOver={gameOver}
                      key={index}
                    />
                  ))}
                  {gameOver ? (
                    <Button disabled style={{ cursor: 'default' }}>You Won 🥇</Button>
                  ) : (
                    <Button onClick={handleSubmitRequest} variant='outlined'>Submit</Button>
                  )}
                  <Keyboard onKeyPress={handleKeyPress} disabledKeys={disabledKeys}/>
                </div>
              )}
              {/* {
                <div style={{background: "#333", width: "100%"}}>
                  <h2>Total Score: {score}</h2>
                </div>
              } */}
            </div>
          )}
      </div>
    );
}

export default QuickGame;
